import { useEditor, EditorContent } from '@tiptap/react'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { NumericInput } from '@/components/ui/numeric-input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Bold, Italic, Link as LinkIcon, Minus, Upload, Loader2, Palette } from 'lucide-react'
import { EMAIL_SAFE_FONTS, FONT_SIZES, TEXT_COLORS, EMAIL_DEFAULT_FONT_NAME, EMAIL_DEFAULT_FONT_SIZE, fontFamilyDisplayName, cleanEmailHtml, EDITOR_CANVAS_STYLE, EDITOR_CANVAS_PARAGRAPH_CLASS, buildEmailEditorExtensions, emailEditorProps } from '@/lib/emailEditorConfig'
import { importSignatureImage, isSignatureImageUrl, measureImageWidth, SIGNATURE_IMAGE_TYPES, uploadSignatureImage } from '@/lib/signatureImages'
import { defaultImageWidth, resizeAllImages } from '@/components/sequences/extensions/ImageUpload'
import { useToast } from '@/hooks/use-toast'

interface SignatureEditorProps {
  value: string
  onChange: (html: string) => void
  /** true finché upload o import di immagini sono in corso: salvare ora perderebbe le immagini (#263). */
  onBusyChange?: (busy: boolean) => void
}

/** Valore del campo larghezza finché le immagini non hanno una width. */
const FALLBACK_IMAGE_WIDTH = 150

function cleanHtml(html: string): string {
  return cleanEmailHtml(html)
}

export function SignatureEditor({ value, onChange, onBusyChange }: SignatureEditorProps) {
  const { t } = useTranslation('settings')
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Contatore, non booleano: con pulsante + paste in parallelo lo spinner
  // deve restare acceso fino alla fine dell'ultimo lavoro (#263).
  const [pendingImages, setPendingImages] = useState(0)
  const uploading = pendingImages > 0

  // Pulsante, incolla e trascina (upload e import delle immagini remote)
  // passano tutti da qui fino all'inserimento nel documento.
  const trackImageWork = useCallback(async <T,>(work: Promise<T>): Promise<T> => {
    setPendingImages((n) => n + 1)
    try {
      return await work
    } finally {
      setPendingImages((n) => n - 1)
    }
  }, [])

  useEffect(() => {
    if (!uploading || !onBusyChange) return
    onBusyChange(true)
    return () => onBusyChange(false)
  }, [uploading, onBusyChange])

  const reportRemovedImages = useCallback(
    (count: number) => {
      toast({
        variant: 'warning',
        title: t('editor.pastedImagesRemovedTitle', { count }),
        description: t('editor.pastedImagesRemovedDescription', { count }),
      })
    },
    [t, toast],
  )

  const reportUploadError = useCallback(
    (error: unknown) => {
      alert((error instanceof Error && error.message) || t('editor.uploadFailed'))
    },
    [t],
  )

  const extensions = useMemo(
    () => buildEmailEditorExtensions({
      placeholder: t('editor.placeholder'),
      images: true,
      richBlocks: false,
      // L'immagine finisce sull'endpoint di upload, mai come data URI nel
      // contenuto salvato (#250); quelle http(s) esterne incollate (Gmail,
      // Outlook web) il backend le scarica sul nostro storage (#262).
      uploadImage: uploadSignatureImage,
      onImageUploadError: reportUploadError,
      importImage: importSignatureImage,
      isStoredImage: isSignatureImageUrl,
      onPastedImagesRemoved: reportRemovedImages,
      measureImageWidth,
      trackImageWork,
    }),
    [t, reportUploadError, reportRemovedImages, trackImageWork],
  )

  const editor = useEditor({
    // TipTap v3 non ri-renderizza sui cambi di selezione di default:
    // senza questo la toolbar non riflette font/size del testo su cui clicchi (#228).
    shouldRerenderOnTransaction: true,
    extensions,
    editorProps: emailEditorProps,
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(cleanHtml(editor.getHTML()))
    },
  })

  const syncValue = useCallback(() => {
    if (!editor) return
    // value è sempre la forma pulita (onUpdate → cleanHtml) e la pulizia normalizza
    // il grezzo di TipTap (hex → rgb, quoting dei font): confrontare il grezzo farebbe
    // scattare setContent a ogni update, resettando selezione e mark (#228).
    const current = cleanHtml(editor.getHTML())
    if (value !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false })
    }
  }, [editor, value])

  useEffect(() => { syncValue() }, [syncValue])

  if (!editor) return null

  const addLink = () => {
    const url = prompt(t('editor.urlPrompt'))
    if (url) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    try {
      // Come per incolla/trascina: uno screenshot retina non entra a piena dimensione (#263).
      const { src, width } = await trackImageWork((async () => {
        const src = await uploadSignatureImage(file)
        return { src, width: await defaultImageWidth(src, measureImageWidth) }
      })())
      if (editor.isDestroyed) return
      editor.chain().focus().setImage({ src, width: width ?? undefined }).run()
    } catch (error) {
      reportUploadError(error)
    }
  }

  const images: ProseMirrorNode[] = []
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'image') images.push(node)
  })
  const hasImages = images.length > 0
  // Il campo mostra la width della prima immagine: quella che l'utente vede.
  const imageWidth = Number(images[0]?.attrs.width) || FALLBACK_IMAGE_WIDTH

  return (
    <div className="space-y-2">
      <div className="border rounded-md overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 p-1 border-b bg-muted/30">
          <Select
            value={fontFamilyDisplayName(editor.getAttributes('textStyle').fontFamily) || EMAIL_DEFAULT_FONT_NAME}
            onValueChange={(value) => {
              if (value) {
                editor.chain().focus().setFontFamily(value).run()
              } else {
                editor.chain().focus().unsetFontFamily().run()
              }
            }}
          >
            <SelectTrigger className="h-7 w-[120px] text-xs border-0 bg-transparent">
              <SelectValue placeholder={t('editor.fontPlaceholder')} />
            </SelectTrigger>
            <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
              {EMAIL_SAFE_FONTS.map((font) => (
                <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                  {font}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={editor.getAttributes('textStyle').fontSize || EMAIL_DEFAULT_FONT_SIZE}
            onValueChange={(value) => {
              if (value) {
                editor.chain().focus().setFontSize(value).run()
              } else {
                editor.chain().focus().unsetFontSize().run()
              }
            }}
          >
            <SelectTrigger className="h-7 w-[72px] text-xs border-0 bg-transparent">
              <SelectValue placeholder={t('editor.sizePlaceholder')} />
            </SelectTrigger>
            <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
              {FONT_SIZES.map((size) => (
                <SelectItem key={size} value={size}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="w-px h-4 bg-border mx-1" />
          <Button
            type="button"
            variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 w-7 p-0"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 w-7 p-0"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant={editor.isActive('link') ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 w-7 p-0"
            onMouseDown={(e) => e.preventDefault()}
            onClick={addLink}
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0">
                <Palette className="h-3.5 w-3.5" style={{ color: editor.getAttributes('textStyle').color || undefined }} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="start" onCloseAutoFocus={(e) => e.preventDefault()}>
              <div className="grid grid-cols-5 gap-1">
                {TEXT_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    title={c.name}
                    className="h-6 w-6 rounded border border-border hover:scale-110 transition-transform"
                    style={{ backgroundColor: c.value }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => editor.chain().focus().setColor(c.value).run()}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <div className="w-px h-4 bg-border mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {t('editor.image')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={SIGNATURE_IMAGE_TYPES.join(',')}
            className="hidden"
            onChange={handleImageUpload}
          />
        </div>

        {/* Editor */}
        <EditorContent
          editor={editor}
          style={EDITOR_CANVAS_STYLE}
          className={`max-w-none p-3 min-h-[80px] focus-within:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[60px] ${EDITOR_CANVAS_PARAGRAPH_CLASS} [&_img]:max-w-full [&_img]:rounded`}
        />
      </div>

      {/* Image resize control */}
      {hasImages && (
        <div className="flex items-center gap-2 text-sm">
          <Label className="text-xs text-muted-foreground">{t('editor.imageWidthLabel')}</Label>
          <NumericInput
            value={imageWidth}
            onChange={(width) => resizeAllImages(editor, width)}
            disabled={uploading}
            min={40}
            max={600}
            className="w-20 h-7"
          />
          <span className="text-xs text-muted-foreground">px</span>
        </div>
      )}
    </div>
  )
}
